/**
 * Kabinet yon menyusi.
 *
 * Bu — sahifa tuzilmasi, ma'lumot emas: bo'limlar ro'yxati qat'iy, ammo
 * yonidagi hisoblagichlar jonli ma'lumotdan qo'yiladi (`key` bo'yicha:
 * `events` — yopilmagan hodisalar, `appeals` — ochiq murojaatlar).
 *
 * Har bir bo'lim o'z marshrutiga ega (`to`) va foydalanuvchi huquqiga qarab
 * filtrlanadi (`perm`): huquq yo'q bo'lsa bo'lim menyuda ko'rinmaydi va
 * sahifasi «huquq yetarli emas» holatini ko'rsatadi. `super_admin` da barcha
 * huquqlar bo'lgani uchun u to'liq menyuni ko'radi.
 */
import type { IconName } from "@/shared/ui/Icon";

export interface NavItem {
  icon: IconName;
  label: string;
  to: string;
  /** Backenddagi huquq kodi (`GET /auth/roles/permissions/`); massiv — istalgan biri yetarli. */
  perm?: string | string[];
  /** Jonli hisoblagich kaliti (`events` / `appeals`). */
  key?: "events" | "appeals";
}

export interface NavGroup {
  title: string;
  items: NavItem[];
}

export const navGroups: NavGroup[] = [
  {
    title: "Operativ",
    items: [
      { icon: "chart", label: "Boshqaruv paneli", to: "/kabinet" },
      {
        /* Hodisalar reyestri. Bildirishnomalar — boshqa bo'lim
           (`/kabinet/bildirishnomalar`), u yuqori paneldagi qo'ng'iroq
           belgisidan ochiladi. */
        icon: "activity",
        label: "Hodisalar",
        to: "/kabinet/hodisalar",
        key: "events",
        perm: "monitoring.view_event",
      },
      {
        icon: "message",
        label: "Murojaatlar",
        to: "/kabinet/murojaatlar",
        key: "appeals",
        perm: "kuzatuv.view_appeal",
      },
      {
        icon: "camera",
        label: "Kuzatuv markazi",
        to: "/kabinet/kuzatuv",
        perm: "monitoring.view_camera",
      },
      { icon: "chart", label: "Hisobotlar", to: "/kabinet/hisobotlar", perm: "reports.view" },
      {
        icon: "layers",
        label: "Dashboardlar",
        to: "/kabinet/dashboardlar",
        perm: "reports.view",
      },
    ],
  },
  {
    title: "Geoportal",
    items: [
      { icon: "map", label: "Xaritalar", to: "/kabinet/xaritalar", perm: "gis.view" },
      {
        icon: "tree",
        label: "O'rmon tiklash",
        to: "/kabinet/tiklash",
        perm: "afforestation.view",
      },
    ],
  },
  {
    /* Ekologiya — НСМЛ vazifalari: bioxilma-xillik, uglerod balansi va
       ekologik analitika (ilgari bitta sahifa edi). */
    title: "Ekologiya",
    items: [
      {
        icon: "leaf",
        label: "Bioxilma-xillik",
        to: "/kabinet/ekologiya/bioxilma-xillik",
        perm: "gis.view_biodiversity",
      },
      {
        icon: "globe",
        label: "Uglerod balansi",
        to: "/kabinet/ekologiya/uglerod",
        perm: "gis.view",
      },
      {
        icon: "chart",
        label: "Ekologik analitika",
        to: "/kabinet/ekologiya/analitika",
        perm: "gis.view_biodiversity",
      },
    ],
  },
  {
    title: "Xo'jalik",
    items: [
      { icon: "briefcase", label: "Ijara", to: "/kabinet/ijara", perm: "lease.view" },
      {
        icon: "file",
        label: "Billing va invoys",
        to: "/kabinet/billing",
        perm: "billing.view_contract",
      },
      { icon: "package", label: "Mahsulotlar", to: "/kabinet/mahsulotlar", perm: "products.view" },
      { icon: "store", label: "Yashil olam", to: "/kabinet/bozor", perm: "market.view" },
    ],
  },
  {
    title: "Boshqaruv",
    items: [
      {
        icon: "building",
        label: "Korporativ kabinet",
        to: "/kabinet/korporativ",
        /* Xat, yig'ilish, kadrlar va arxiv — har biri alohida rol (arxivchi, konferensiya admini...). */
        perm: [
          "corporate.send_message",
          "corporate.view_archive",
          "corporate.manage_meeting",
          "corporate.manage_hr",
        ],
      },
      {
        icon: "link",
        label: "Integratsiyalar",
        to: "/kabinet/integratsiyalar",
        perm: "admin.manage_integrations",
      },
      {
        icon: "shield",
        label: "Foydalanuvchilar",
        to: "/kabinet/foydalanuvchilar",
        perm: "admin.manage_users",
      },
      {
        icon: "scale",
        label: "Raqamli nazorat",
        to: "/kabinet/nazorat",
        /* Prokuror roli `admin.view_audit` emas, `oversight.*` huquqlariga ega. */
        perm: ["admin.view_audit", "oversight.read_all"],
      },
      {
        /* Alohida xizmat konturi: bosilganda kabinetdan chiqib,
           `/superadmin` karkasi ochiladi. */
        icon: "database",
        label: "Superadmin paneli",
        to: "/superadmin",
        perm: "admin.db_view",
      },
    ],
  },
];

/** `perm` — bitta kod yoki kodlar ro'yxati (istalgani yetarli); bo'sh bo'lsa — hammaga ochiq. */
export function hasAnyPerm(permissions: string[], perm: string | string[] | undefined): boolean {
  if (!perm) return true;
  return (Array.isArray(perm) ? perm : [perm]).some((code) => permissions.includes(code));
}

/**
 * Huquqlar bo'yicha filtrlangan menyu.
 * Profil hali yuklanmagan bo'lsa (`permissions` bo'sh) to'liq menyu qaytariladi —
 * aks holda sahifa yangilanganda menyu «sakraydi».
 */
export function visibleGroups(permissions: string[] | undefined): NavGroup[] {
  if (!permissions || permissions.length === 0) return navGroups;
  return navGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => hasAnyPerm(permissions, item.perm)),
    }))
    .filter((group) => group.items.length > 0);
}
